import React, { Fragment } from 'react';
import cls from 'classnames';
import {
    faFileContract,
    faSave,
    faSort,
    faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Button,
    ButtonGroup,
    Input,
    FormGroup,
    Row,
    Col
} from 'reactstrap';
import { Prompt, withRouter, Link } from 'react-router-dom';
import { isEqual } from 'lodash-es';
import {
    AppPageForm,
    FlexCenterRow,
    FlexEndRow,
    onFieldChange,
    onReactSelectChanged,
    SimpleTable,
    SmallButton,
    SmallOutlineButton,
    SubHeading,
    toasty,
    YesNoBadge,
    FormLabel,
    FlexStartRow
} from '../common/forms/FormElements';
import { BaseFormViewModel } from '../common/ViewModel';
import { Can } from '../Can';
import CommonContext, {
    ApiRoutes,
    AppNavPaths,
    ServiceResponseResult,
    LocalizationKeys as l,
    YesNoOptions,
} from '../Common';
import {
    Contract,
    ScheduleTypes,
    WorkOrderNumberOptions,
    ChargeTypeEligbility,
    ChargeTypeEligibilities,
    _ContractStatuses,
} from './Contract';
import { util } from '../Util';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { handleFormSaveError } from '../common/forms/ValidationError';
import ChargeTypeSelect from '../chargeType/ChargeTypeSelect';
import ReorderListWidget from '../widgets/ReorderListWidget';
import SlideForm from '../common/forms/SlideForm';
import {
    ChargeTypeConfigurationType,
    _ContractChargeTypeEligibility,
} from '../chargeType/ChargeType';
import { TimesheetResponsibilityType } from '../tenant/Tenant';
import authService from '../api-authorization/AuthorizeService';
import EmailRecipients from '../common/forms/EmailRecipients';
import { Address } from '../address/Address';
import AddressFormNew from '../address/AddressFormNew';
import { v4 as uuid } from 'uuid';
import { Organization } from '../organization/Organization';

class ContractForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.chargeTypeSelectorRef = React.createRef();
        this.payScheduleSelectorRef = React.createRef();
        this.chargeTypesWidgetRef = React.createRef();
        this.rateFormRef = React.createRef();
        this.addressFormRef = React.createRef();

        const stateBase = {
            contract: new Contract(),
            contractStatuses: [],
            customerChargeTypes: [],
            dispatchLocations: [],
            fromTemplate: true,
            operationsCenters: [],
            originalData: null,
            scheduleTypes: ScheduleTypes,
            selectedChargeType: null,
            selectedJob: null,
            selfDispatchOptions: YesNoOptions,
            selectedSortableChargeTypes: [],
            showReorderWidget: false,
            workOrders: [],
            currentEmailAddress: null,
            invoiceTerms: [],
            invoiceMappings: [],
            chargeTypeGroups: [],
            enableContractDateRange: false,
            expirationGracePeriodOptions: [],
            enableAutoEscalatingContractRates: false,
            autoEscalatingRateOptions: YesNoOptions,
            enablePrevailingWageContracts: false,
            prevailingWageOptions: YesNoOptions,
            haulingLocations: [],
            selectedHaulingFromLocation: null,
            selectedHaulingToLocation: null,
            globalLocationTarget: null,
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
        this.onChange = this.onChange.bind(this);
        this.onSelectChanged = this.onSelectChanged.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onScheduleTypeChanged = this.onScheduleTypeChanged.bind(this);
        this.onAllowSelfDispatchChanged = this.onAllowSelfDispatchChanged.bind(this);
        this.onAllowLinkedWorkOrdersChanged = this.onAllowLinkedWorkOrdersChanged.bind(this);
        this.handleSaveError = this.handleSaveError.bind(this);
        this.getContractTimesheetResponsibilityTypeOptions = this.getContractTimesheetResponsibilityTypeOptions.bind(this);
        this.onAutoEscalatingRatesChanged = this.onAutoEscalatingRatesChanged.bind(this);
        this.onPrevailingWageChanged = this.onPrevailingWageChanged.bind(this);
    }

    // #region LIFECYCLE EVENTS
    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevProps
            && this.props.match.params.id !== (prevProps.match.params ?? {}).id
        ) {
            this.populateState();
        }
    }

    componentWillUnmount() {
        authService.unsubscribe(this._subscription);
    }

    onAddCTGroup = () => {
        const { contract, selectedCTGroups } = { ...this.state };

        let updated = [...contract.contractChargeTypeGroups] ?? [];

        // get selections from the popup
        const selectedChargeTypes = selectedCTGroups.map((x, index) => ({
            companyId: contract.id,
            chargeTypeGroupId: x.chargeTypeGroup.id,
            invoiceDescription: x.invoiceDescription,
            billRate: x.billRate,
            chargeTypeGroup: x,
        }));

        // merge additions with current charges
        updated = updated.concat([...selectedChargeTypes]);

        contract.contractChargeTypeGroups = [...updated];

        this.setState({
            contract: { ...contract },
            showCTGroupModal: false,
            selectedCTGroups: [],
        });
    };

    // #region CHARGE TYPES
    onAddChargeTypes = () => {
        const { contract } = { ...this.state };
        const { contractChargeTypes } = { ...contract };
        const typesToOmit = (contractChargeTypes ?? []).map((c) => c.chargeTypeId) ?? [];
        // pass in types already selected
        this.chargeTypeSelectorRef.current.open(null, [...typesToOmit]);
    };

    onAddChargeTypesCallback = (selections) => {
        const { contract } = { ...this.state };
        let updated = [...contract.contractChargeTypes] ?? [];

        // get selections from the popup
        const selectedChargeTypes = [...selections].map((x, index) => ({
            order: null /* append to the end, user can then order them */,
            contractTemplateId: contract.id,
            chargeTypeId: x.id,
            chargeTypeEligibilityId: 1,
            description: x.description,
            chargeUnits: x.unitsName,
            group: x.appliesToGroup,
            employees: x.appliesToUsers,
            equipment: x.appliesToEquipment,
            billable: x.isBillable,
            resourceTypeId: x.resourceTypeId,
            isSurcharge: x.isSurcharge,
            isPermit: x.isPermit,
            costCodeId: x.costCodeId
        }));

        // merge additions with current charges
        updated = updated.concat([...selectedChargeTypes]);

        // reorder everything after the add
        updated = updated.map((x, index) => ({ ...x, ...{ order: (index += 1) } }));

        contract.contractChargeTypes = [...updated];

        this.setState({ contract: { ...contract } });
    };

    onAddJob = () => {
        this.props.history.push(
            `${AppNavPaths.JobNew}/${this.props.match.params.id}`,
        );
    };

    onAddPayScheduleCallback = (selections) => {
        const { contract } = { ...this.state };
        let updated = [...contract.contractPaySchedules] ?? [];

        // get selections from the popup
        const selectedPaySchedules = [...selections].map((x, index) => ({
            id: 0,
            contractId: contract.id,
            payScheduleId: x.id,
            paySchedule: {
                id: x.id,
                governingBody: x.governingBody,
                state: x.state,
                counties: x.counties,
                reg: x.reg,
                ot: x.ot,
                fringe: x.fringe,
            },
        }));

        const contractHasStateOnlyPS = contract.contractPaySchedules.filter(
            (x) => x.paySchedule.counties == null
                || x.paySchedule.counties.length == 0,
        );
        const selectionsStateOnlyPS = selectedPaySchedules.filter(
            (x) => x.paySchedule.counties == null
                || x.paySchedule.counties.length == 0,
        );
        const contractHasItems = contract.contractPaySchedules != null
            && contract.contractPaySchedules.length > 0;

        // Contract has a state only pay schedule, nothing else can be added
        if (contractHasStateOnlyPS?.length > 0) {
            toasty.error(
                'Pay Schedule Conflict',
                'There is a pay schedule for the entire state. No more pay schedules can be added.',
            );
            return;
        }

        // Selections contain a state only pay schedule, but contract already has a pay schedule
        if (selectionsStateOnlyPS.length > 0 && contractHasItems) {
            toasty.error(
                'Pay Schedule Conflict',
                'Unable to add pay schedule for the entire state due to an existing pay schedule.',
            );
            return;
        }

        // Selections contain a state only pay schedule, with multiple selections
        if (
            selectionsStateOnlyPS.length > 0
            && selectedPaySchedules.length > 1
        ) {
            toasty.error(
                'Pay Schedule Conflict',
                'Cannot add other pay schedules with a pay schedule for the entire state.',
            );
            return;
        }

        // merge additions with current charges
        updated = updated.concat([...selectedPaySchedules]);
        contract.contractPaySchedules = [...updated];

        this.setState({ contract: { ...contract } });
    };

    // #endregion

    onAllowLinkedWorkOrdersChanged = (value) => {
        const { contract } = { ...this.state };
        contract.allowLinkedWorkOrders = value;
        this.setState({ contract });
    };

    onAllowNonFlaggingChanged = (value) => {
        const { contract } = { ...this.state };
        contract.enableNonFlaggingTimesheets = value;
        // FOR NOW, PER CW:
        // Hide the allow self-dispatch button (since self-dispatch isn't yet finished)
        // and default to true when the non-flag contract is set up.
        contract.allowSelfDispatch = !!value;
        this.setState({ contract });
    };

    onAllowSelfDispatchChanged = (value) => {
        const { contract } = { ...this.state };
        contract.allowSelfDispatch = value;
        // When disabling self-dispatch, non-flag should also be disabled.
        if (!value) {
            contract.enableNonFlaggingTimesheets = false;
        }
        this.setState({ contract });
    };

    onAutoEscalatingRatesChanged = (value) => {
        const { contract } = { ...this.state };
        contract.autoEscalatingRates = value;
        this.setState({ contract });
    };

    // #endregion

    // #region BASE FORM EVENTS
    onChange = onFieldChange;

    onClose = (response) => {
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    };

    /**
     * When the customer selection changes, populate customer specific inputs.
     *
     * @param {any} customer
     */
    onCustomerChanged = async (customer) => {
        const { contract } = this.state;
        const companyId = (customer ?? {}).value ?? null;

        contract.companyId = companyId;

        if (companyId) {
            const chargeTypes = await this.getChargeTypesForCustomer(companyId);
            contract.chargeTypes = chargeTypes;

            contract.hasAlternativePayroll = false;
            contract.operationsCentersApply = false;

            let billingContacts = [];

            if (contract.companyId) {
                const [contacts] = await Promise.all([
                    util.fetch
                        .js(
                            ApiRoutes.typeAheads.billingContacts(
                                contract.companyId,
                            ),
                        )
                        .catch((error) => console.log('Unable to get the billing contacts.')),
                ]);
                if (contacts) {
                    billingContacts = contacts.map((x) => ({ label: x.label, value: x.value }));
                }
            }

            this.setState({
                contract,
                operationsCenters: [],
                billingContacts,
                customerChargeTypes: chargeTypes,
            });
        } else {
            contract.invoiceTermsId = null;
            contract.invoiceMappingId = null;
            contract.chargeTypes = [];
            contract.hasAlternativePayroll = false;
            contract.operationsCentersApply = false;

            this.setState({
                contract: contract,
                operationsCenters: [],
                billingContacts: [],
                customerChargeTypes: [],
            });
        }
    };

    onDelete = async (e) => {
        const { contract } = this.state;
        const response = await util.fetch.delete(
            ApiRoutes.contract.delete(contract.id),
        );
        if (response) this.onClose(response);
    };
    // #endregion

    onEmailAdded = (e) => {
        const { currentEmailAddress, contract } = { ...this.state };
        if (currentEmailAddress) {
            const emailAddress = currentEmailAddress.trim();
            const match = util.validation.email(emailAddress);
            if (match) {
                if (!(contract.timesheetEmailRecipients ?? []).length) contract.timesheetEmailRecipients = [];
                if (!contract.timesheetEmailRecipients.includes(emailAddress)) contract.timesheetEmailRecipients.push(emailAddress);
                this.setState({
                    contract: { ...contract },
                    currentEmailAddress: null,
                });
            } else {
                toasty.error('Please enter a single valid email address.');
            }
        }
    };

    // #endregion

    // #region EMAIL
    onEmailChanged = (e) => {
        const val = e.currentTarget.value;
        this.setState({ currentEmailAddress: val });
    };

    onEmailRemoved = (selected) => {
        const { contract } = { ...this.state };
        const inx = contract.timesheetEmailRecipients.findIndex(
            (ea) => ea === selected,
        );
        contract.timesheetEmailRecipients.splice(inx, 1);
        this.setState({ contract });
    };

    onPermitFeesApplyChanged = (value) => {
        const { contract } = { ...this.state };
        contract.permitFeesApply = value;
        this.setState({ contract });
    };

    onPrevailingWageChanged = (value) => {
        const { contract } = { ...this.state };
        contract.prevailingWage = value;
        this.setState({ contract });
    };

    onRemoveChargeType = (chargeTypeIndex) => {
        const { contract } = { ...this.state };
        const chargeTypes = [...contract.contractChargeTypes];
        chargeTypes.splice(chargeTypeIndex, 1);
        contract.contractChargeTypes = [...chargeTypes];
        this.setState({ contract: { ...contract } });
    };

    onReorderChargeTypes = () => {
        // clone the list of charge types, to avoid mutating state.
        // pass to the widget.
        this.setState((state) => {
            const { contract } = { ...state };
            const selectedSortableChargeTypes = contract.contractChargeTypes;
            return {
                showReorderWidget: true,
                selectedSortableChargeTypes,
            };
        });
    };

    onRequireSelfDispatchTimesheetsClientSignatureChanged = (value) => {
        const { contract } = { ...this.state };
        contract.requireSelfDispatchTimesheetsClientSignature = value;
        this.setState({ contract });
    };

    onScheduleTypeChanged = (id) => {
        const { contract } = { ...this.state };
        contract.contractScheduleTypeId = id;
        this.setState({ contract });
    };

    onSelectChanged = onReactSelectChanged;


    onSubmit = async (e) => {
        const { contract } = { ...this.state };

        // Revert location uuids
        if (contract.haulFromLocation != null) {
            contract.haulFromLocation.id = contract.haulFromLocation.id.constructor === String
                ? null
                : contract.haulFromLocation.id;
        }

        if (contract.haulToLocation != null) {
            contract.haulToLocation.id = contract.haulToLocation.id.constructor === String
                ? null
                : contract.haulToLocation.id;
        }

        // Clear any fluent api errors
        this.setState((state) => ({ errors: {}, saving: true }));

        !contract.id ? this.create(contract) : this.update(contract);
    };

    // Customer Exceptions
    getChargeTypesForCustomer = async (customerId) => {
        const response = await util.fetch
            .js(ApiRoutes.chargeType.byCustomer(customerId))
            .catch((err) => {
                console.log(
                    `Failed to get charge types for selected customer: ${err.toString()}`,
                );
            });
        return response ?? [];
    };

    // #endregion

    getContractTimesheetResponsibilityTypeOptions(crewLead) {
        // Moved from Tenant.js since we're using localized strings now.

        const ret = [
            {
                id: 1,
                name: crewLead,
                description: `${crewLead}s are responsible for all timesheet entry.`,
            },
            {
                id: 2,
                name: 'Individual',
                description: 'Individual employees enter timesheets.',
            },
        ];

        return ret;
    }

    getDefaultMapping = async (companyId) => {
        const response = await util.fetch
            .js(ApiRoutes.company.defaultMapping(companyId))
            .catch((err) => {
                console.log('No mapping setup for selected customer}');
            });
        return response ?? null;
    };

    getDefaultTerms = async (companyId) => {
        const response = await util.fetch
            .js(ApiRoutes.company.defaultTerms(companyId))
            .catch((err) => {
                console.log('No terms setup for selected customer}');
            });
        return response ?? null;
    };

    getOperationsCentersForCustomer = async (customerId) => {
        const response = await util.fetch
            .js(
                ApiRoutes.company.customerOperationsCentersCompanies(customerId),
            )
            .catch((err) => {
                console.log(
                    `Failed to get Operations Centers for selected customer: ${err.toString()}`,
                );
            });
        if (response) {
            return util.select.mapToOptions(response, 'locationName', 'id');
        }
        return [];
    };

    /**
     * A location is GPS-only when there is no street address to display but we
     * do have coordinates instead.  A missing city on its own is not enough -
     * that just means the record didn't carry one.
     *
     * @param {any} location an address-shaped object
     */
    isGpsOnly = (location) => !!location
        && !location.address1
        && !location.city
        && !!(location.latitude || location.longitude);

    /**
     * Hauling location records come back from the type-ahead shaped as
     * { id, company: { companyName, address1, city, state, zip, ... } }.
     * The Haul From / Haul To display underneath each dropdown, and the
     * contract payload itself, both expect a flat address, so normalize
     * the selection before it is assigned to the contract.
     *
     * @param {any} location a record from state.haulingLocations
     */
    haulingLocationToAddress = (location) => {
        if (!location) return null;

        const company = location.company ?? {};

        // Depending on the endpoint, the address fields come back either on the
        // company record or flattened onto the location itself, so check both.
        const pick = (field) => company[field] ?? location[field] ?? null;

        const address1 = pick('address1');
        const city = pick('city');
        const latitude = pick('latitude');
        const longitude = pick('longitude');

        return {
            id: location.id,
            locationName: company.companyName
                ?? company.name
                ?? location.locationName
                ?? location.name,
            address1,
            address2: pick('address2'),
            city,
            state: pick('state'),
            zip: pick('zip'),
            latitude,
            longitude,
            gpsOnly: this.isGpsOnly({
                address1, city, latitude, longitude,
            }),
        };
    };

    /**
     * Label for the hauling location dropdowns.  Guarded because ad-hoc
     * addresses (entered through AddressFormNew) have no company record.
     */
    haulingLocationLabel = (option) => option?.company?.companyName
        ?? option?.locationName
        ?? '';

    /**
     * Match a location already on the contract back to a record in the hauling
     * location list so the dropdown can show it as the current selection on
     * load.
     *
     * Deliberately tries several keys.  The id on the contract's location is
     * not always the hauling location's id, and a location that was hand-
     * entered through the address form carries an address but no name, so
     * matching on any single field leaves one of the two dropdowns empty.
     */
    findHaulingLocation = (locations, location) => {
        if (!location) return null;

        const list = locations ?? [];
        if (!list.length) return null;

        // 1. Same record.
        if (location.id != null) {
            const byId = list.find((x) => x.id === location.id);
            if (byId) return byId;
        }

        const norm = (value) => (value ?? '').toString().trim().toLowerCase();

        // Normalize both sides so it doesn't matter whether the fields sit on
        // the record itself or on its nested company.
        const target = this.haulingLocationToAddress(location);
        const candidates = list.map((option) => ({
            option,
            address: this.haulingLocationToAddress(option),
        }));

        // 2. Same name.
        if (norm(target.locationName)) {
            const byName = candidates.find(
                (c) => norm(c.address.locationName) === norm(target.locationName),
            );
            if (byName) return byName.option;
        }

        // 3. Same street address, for locations that carry no name.
        if (norm(target.address1)) {
            const byAddress = candidates.find((c) => {
                if (norm(c.address.address1) !== norm(target.address1)) return false;

                // Only let the zip veto a match when both sides have one.
                if (norm(target.zip) && norm(c.address.zip)) {
                    return norm(c.address.zip) === norm(target.zip);
                }

                return true;
            });
            if (byAddress) return byAddress.option;
        }

        // 4. Same coordinates, for GPS-only locations.
        if (target.latitude != null && target.longitude != null) {
            const byCoords = candidates.find(
                (c) => c.address.latitude != null
                    && c.address.longitude != null
                    && Number(c.address.latitude) === Number(target.latitude)
                    && Number(c.address.longitude) === Number(target.longitude),
            );
            if (byCoords) return byCoords.option;
        }

        return null;
    };

    create = async (contract) => {
        contract.effectiveDate = null;
        contract.expirationDate = null;

        const response = await util.fetch.andGetResponse(
            util.fetch.types.post,
            ApiRoutes.contract.create(),
            { ...contract },
            'Error Creating Job',
        );

        if (response.result === ServiceResponseResult.Ok) {
            toasty.success('Job created.');
            this.props.history.push(`${AppNavPaths.Contract}/${response.data}`);
        } else {
            toasty.error('Problem saving the job:', response.message, {
                autoClose: false,
                closeOnClick: true,
            });
        }

        this.setState({ saving: false });
    };

    // #endregion

    // #region SUBMISSION
    handleSaveError = handleFormSaveError;

    notifyError = () => toasty.error(
        'Save Unsuccessful',
        'There was a server error saving this job.  Please try your request again or contact support for assistance.',
    );

    notifySuccess = () => toasty.success('Job Saved', 'Job saved successfully.');

    async populateState() {
        const isAuthenticated = await authService.isAuthenticated();

        if (isAuthenticated) {
            const contract_id = this.props.match.params.id;

            // RLC: A note on this: This will not always be available because React's context is async.
            // It also bears no events for when it is ready, either.
            const { user } = this.context;

            // RLC: This is ultimately unused in the service, and probably should be cleaned up.
            // All tenant customers are returned.
            const companyId = (user ?? {}).companyId ?? 1;

            const [
                strings,
                customers,
                dispatchLocations,
                contractStatuses,
                contract,
                contractTemplates,
                chargeTypes,
                materials,
                costCodes
            ] = await Promise.all([
                util.l10n.getStrings([l.crewLead]),
                util.fetch
                    .js(ApiRoutes.company.customerCompanies(companyId))
                    .catch((error) => console.log('Unable to get customers.')),
                util.fetch
                    .js(ApiRoutes.company.dispatchCompanies())
                    .catch((error) => console.log('Unable to get dispatch dispatchLocations.')),
                util.fetch
                    .js(ApiRoutes.typeAheads.contractStatuses())
                    .catch((error) => console.log('Unable to get job statuses.')),
                contract_id
                    ? util.fetch
                        .js(ApiRoutes.contract.byId(contract_id))
                        .catch((error) => console.log('Unable to get job.'))
                    : new Contract(),
                util.fetch.js(ApiRoutes.contractTemplate.names()),
                util.fetch
                    .js(ApiRoutes.typeAheads.chargeTypesBillable())
                    .catch((error) => console.log('Unable to get charge types.')),
                util.fetch
                    .js(ApiRoutes.typeAheads.materials())
                    .catch((error) => console.log('Unable to get materials.')),
                util.fetch
                    .js(ApiRoutes.typeAheads.costCodes())
            ]);

            const glStringsEnabled = Boolean(
                this.context.tenant?.tenantSettings?.glStringsEnabled,
            );

            const enableContractDateRange = Boolean(
                this.context.tenant?.tenantSettings?.enableContractDateRange,
            );

            const enableAutoEscalatingContractRates = Boolean(
                this.context.tenant?.tenantSettings
                    ?.enableAutoEscalatingContractRates,
            );

            const enablePrevailingWageContracts = Boolean(
                this.context.tenant?.tenantSettings
                    ?.enablePrevailingWageContracts,
            );

            const warningThresholdOptions = [
                { label: '30', value: 30 },
                { label: '60', value: 60 },
                { label: '90', value: 90 },
            ];

            let operationsCenters = [];
            let haulingLocations = [];
            let selectedHaulingFromLocation = null;
            let selectedHaulingToLocation = null;

            if (contract_id) {

                haulingLocations = await util.fetch.js(ApiRoutes.typeAheads.haulingLocations(contract_id));

                if (contract.companyId) {

                    const [centers] = await Promise.all([
                        util.fetch
                            .js(
                                ApiRoutes.company.customerOperationsCentersCompanies(
                                    contract.companyId,
                                ),
                            )
                            .catch((error) => console.log(
                                'Unable to get the operations centers.',
                            )),
                    ]);
                    if (centers) {
                        operationsCenters = centers.map((x) => ({ label: x.companyName, value: x.id }));
                    }
                }


                // A GPS-only location comes back with a null city, not an empty
                // string, so a length check never fired here.  Use the same
                // rule the dropdown mapping uses.
                if (contract.haulFromLocation) {
                    contract.haulFromLocation.gpsOnly = this.isGpsOnly(
                        contract.haulFromLocation,
                    );
                }
                if (contract.haulToLocation) {
                    contract.haulToLocation.gpsOnly = this.isGpsOnly(
                        contract.haulToLocation,
                    );
                }

                // Seed the dropdown selections so an existing contract doesn't
                // render with empty hauling location selects.  Matching on the
                // id alone missed any location that was hand-entered rather
                // than picked from the list.
                selectedHaulingFromLocation = this.findHaulingLocation(
                    haulingLocations,
                    contract.haulFromLocation,
                );

                selectedHaulingToLocation = this.findHaulingLocation(
                    haulingLocations,
                    contract.haulToLocation,
                );
            }

            const originalData = JSON.stringify({ ...contract });

            this.setState({
                haulingLocations,
                selectedHaulingFromLocation,
                selectedHaulingToLocation,
                strings,
                chargeTypes,
                costCodes,
                contractStatuses,
                contractTemplates: [...contractTemplates],
                dispatchLocations: dispatchLocations.map((x) => ({ label: x.companyName, value: x.id })),
                loading: false,
                contract,
                originalData,
                customers: customers.map((x) => ({ label: x.companyName, value: x.id })),
                operationsCenters,
                saving: false,
                enableContractDateRange,
                warningThresholdOptions,
                enableAutoEscalatingContractRates,
                enablePrevailingWageContracts,
                glStringsEnabled,
                materials
            });
        }
    }

    resetForm = () => this.setState({ formValidated: false, saving: false });

    update = async (contract) => {
        const response = await util.fetch.andGetResponse(
            util.fetch.types.put,
            ApiRoutes.contract.update(contract.id),
            { ...contract },
            'Error Saving Job',
        );

        if (response.result === ServiceResponseResult.Ok) {
            toasty.success('Job saved.');

            this.props.history.go(0);
        } else {
            toasty.error('Problem saving the job:', response.message, {
                autoClose: false,
                closeOnClick: true,
            });
        }
        this.setState({ saving: false });
    };

    updateChargeTypeEligibility = (index, eligibilityId) => {
        const { contract } = { ...this.state };
        const chargeType = { ...contract.contractChargeTypes[index] };
        chargeType.chargeTypeEligibilityId = eligibilityId;
        const chargeTypes = [...contract.contractChargeTypes];
        chargeTypes[index] = { ...chargeType };
        contract.contractChargeTypes = [...chargeTypes];
        this.setState({ contract: { ...contract } });
    };

    renderContractChargeTypesRows = () => {
        const { contract, costCodes } = { ...this.state };
        const { contractChargeTypes } = contract;

        if (!contractChargeTypes) return '';

        return contractChargeTypes.map((chargeType, chargeTypeIndex) => {
            // User must select an eligibility
            const eligibilityValid = !!this.state.formValidated
                && !chargeType.chargeTypeEligibilityId;

            // Is this an exception for the currently selected customer on the contract?
            const isException = (
                (
                    (chargeType.configurations ?? []).find(
                        (x) => x.chargeTypeConfigurationTypeId
                            == ChargeTypeConfigurationType.Exception,
                    ) ?? {}
                ).customers ?? []
            ).includes(contract.companyId);

            return (
                <tr key={chargeTypeIndex}>
                    <td className="text-center" style={{ paddingTop: '12px' }}>{chargeType.order ?? ''}</td>
                    <td style={{ paddingTop: '12px' }}>
                        {chargeType.description}
                        {!!isException && (
                            <span className="exception-badge badge badge-warning ml-2">
                                E
                            </span>
                        )}
                    </td>
                    <td className="text-center" style={{ paddingTop: '12px' }}>{chargeType.chargeUnits}</td>
                    <td style={{ paddingTop: '12px' }}>{chargeType.group}</td>
                    <td style={{ paddingTop: '12px' }}>{chargeType.employees}</td>
                    <td>
                        <div style={{ maxHeight: '35px' }}>
                            <ValidatedSelect
                                id="costCode"
                                name="chargeType.costCodeId"
                                options={costCodes}
                                value={
                                    (costCodes ?? []).find(
                                        (x) => x.value
                                            === chargeType.costCodeId,
                                    ) ?? ''
                                }
                                onChange={(evt) => {
                                    contractChargeTypes[chargeTypeIndex].costCodeId = evt == null ? null : evt.value;

                                    this.setState({
                                        contract: contract,
                                    });
                                }}
                            />
                        </div>
                    </td>
                    <td className="text-center" style={{ paddingTop: '12px' }}>
                        <YesNoBadge value={chargeType.billable} />
                    </td>
                    <Can I="edit" a="contract">
                        <CommonContext.Consumer>
                            {(value) => (
                                <>
                                    <td
                                        className={cls({
                                            'p-0': eligibilityValid,
                                        })}
                                        style={{ paddingTop: '12px' }}
                                    >
                                        <div
                                            className={cls('h-100 w-100', {
                                                'p-2 border border-danger':
                                                    eligibilityValid,
                                            })}
                                        >
                                            <ButtonGroup
                                                disabled={
                                                    !!value.formIsOpen
                                                }
                                            >
                                                {ChargeTypeEligibilities.map(
                                                    (
                                                        eligibility,
                                                        eligibilityIndex,
                                                    ) => (
                                                        <Button
                                                            type="button"
                                                            color="secondary"
                                                            size="sm"
                                                            key={
                                                                eligibilityIndex
                                                            }
                                                            className={cls(
                                                                'pt-0 pb-0 pl-1 pr-1 mr-1',
                                                                {
                                                                    active:
                                                                        eligibility.id
                                                                        === chargeType.chargeTypeEligibilityId,
                                                                },
                                                            )}
                                                            onClick={() => {
                                                                this.updateChargeTypeEligibility(
                                                                    chargeTypeIndex,
                                                                    eligibility.id,
                                                                );
                                                            }}
                                                        >
                                                            {
                                                                eligibility.name
                                                            }
                                                        </Button>
                                                    ),
                                                )}
                                            </ButtonGroup>
                                            <input
                                                type="text"
                                                className="hidden-validation"
                                                required
                                                defaultValue={
                                                    chargeType.chargeTypeEligibilityId
                                                    ?? ''
                                                }
                                            />
                                            <span
                                                className="invalid-feedback text-danger"
                                                hidden
                                            >
                                                Eligibility selection is
                                                required.
                                            </span>
                                        </div>
                                    </td>
                                    <td className="text-center">
                                        <FontAwesomeIcon
                                            icon={faTimes}
                                            title={`Remove ${chargeType.description}`}
                                            className="text-danger cursor-pointer"
                                            onClick={() => this.onRemoveChargeType(
                                                chargeTypeIndex,
                                            )}
                                        />
                                    </td>
                                </>
                            )}
                        </CommonContext.Consumer>
                    </Can>
                </tr>
            );
        });
    };


    // #region JOBS
    renderJobRows = () => {
        const jobs = this.state.contract.jobs.filter(
            (x) => x.isDeleted === false,
        );
        const { number } = this.state.contract;
        if (!jobs) return '';
        return jobs.map((job, i) => (
            <tr key={job.id}>
                <td className="ct-job-number">
                    <Link
                        className="site-link"
                        title="View this Job"
                        to={`${AppNavPaths.Job}/${job.id}`}
                    >
                        {`${number}-${job.contractSequence}`}
                    </Link>
                </td>
                <td className="ct-job-name">{job.description}</td>
                <td className="ct-job-week">{job.weekOfDate}</td>
                <td className="ct-job-active">
                    <YesNoBadge value />
                </td>
            </tr>
        ));
    };

    onAddLocation = (type) => {
        const { contract } = this.state;
        const selectedAddress = new Address();

        const addressFormTitle = `${type === 'haulFromLocation' ? 'Haul From' : 'Haul To'} Location`;
        this.setState({
            selectedAddressType: type,
            globalLocationTarget: null,
            addressFormTitle,
        });

        // dont show duplicate location if ones filled out already
        let show = true;
        if (contract.meetingLocation || contract.jobLocation) show = false;

        this.addressFormRef.current.open(
            false,
            selectedAddress,
            addressFormTitle,
            show,
            `Same as ${type === 'meetingLocation' ? 'Haul From' : 'Haul To'
            }`,
        );
    };

    onEditLocation = (type, globalLocationTarget) => {
        const { contract } = { ...this.state };
        const selectedAddress = { ...contract[type] };

        let addressFormTitle = `Edit ${type === 'haulFromLocation' ? 'Haul From' : 'Haul To'} Location`;

        let isGlobalAddress = false;
        if (type == 'globalLocation') {
            addressFormTitle = `Add Job Location`;
            isGlobalAddress = true;
        }

        this.setState({
            selectedAddress,
            selectedAddressType: type,
            globalLocationTarget: globalLocationTarget ?? null,
            addressFormTitle,
            pageModified: true,
        });

        this.addressFormRef.current.open(isGlobalAddress, selectedAddress, addressFormTitle);
    };

    onSaveAddress = async (address) => {
        const {
            contract,
            selectedAddressType,
            globalLocationTarget
        } = this.state;

        if (address.id == undefined) {
            address.id = uuid();
        }

        // A global location is posted to the server, then re-read so that the
        // newly created record can be selected in the dropdown it was added from.
        if (selectedAddressType === 'globalLocation') {
            const org = new Organization();

            org.name = address.locationName;
            org.address1 = address.address1;
            org.city = address.city;
            org.state = address.state;
            org.zip = address.zip;
            org.notes = address.description;
            // Send the coordinates up with the create as well.  The dedicated
            // coords endpoint below still runs, but if the create accepts them
            // the re-read list already carries them and nothing depends on the
            // ordering of the two calls.
            org.latitude = address.latitude ?? null;
            org.longitude = address.longitude ?? null;
            org.types.push(11);

            const contractId = address.global ? -1 : contract.id;

            const priorIds = new Set(
                (this.state.haulingLocations ?? []).map((x) => x.id),
            );

            await util.fetch
                .post(ApiRoutes.company.createHaulingLocation(contractId), org)
                .catch(this.handleSaveError);

            const haulingLocations = await util.fetch.js(
                ApiRoutes.typeAheads.haulingLocations(contract.id),
            );

            // The record that wasn't in the list before the POST is the new one.
            // Fall back to a name match if the ids can't be diffed.
            const created = (haulingLocations ?? []).find(
                (x) => !priorIds.has(x.id),
            ) ?? (haulingLocations ?? []).find(
                (x) => (x.company?.companyName ?? '') === org.name,
            );

            const next = { haulingLocations };

            if (created) {
                await util.fetch
                    .post(ApiRoutes.company.updateHaulingLocationCoords(
                        created.company.id,
                        address.latitude,
                        address.longitude,
                    ))
                    .catch(this.handleSaveError);

                // The list above was read before the coordinates were saved, so
                // the record we just found has no lat/lng on it.  Graft the
                // values the user entered on locally - without them
                // haulingLocationToAddress can never classify a location with
                // no street address as GPS-only.
                created.company = {
                    ...(created.company ?? {}),
                    latitude: address.latitude ?? null,
                    longitude: address.longitude ?? null,
                };

                const isFrom = globalLocationTarget !== 'haulingToLocation';

                contract[isFrom ? 'haulFromLocation' : 'haulToLocation'] = this.haulingLocationToAddress(created);

                next.contract = { ...contract };
                next[
                    isFrom
                        ? 'selectedHaulingFromLocation'
                        : 'selectedHaulingToLocation'
                ] = created;
            }

            this.setState(next);

            return;
        }

        contract[selectedAddressType] = { ...address };

        if (address.duplicate) {
            // if the other is blank fill it in.
            if (selectedAddressType === 'haulFromLocation' && !contract.haulToLocation) {
                const mAddress = util.object.clone(address);
                mAddress.id = uuid();

                contract.haulToLocation = mAddress;
            }

            if (selectedAddressType === 'haulToLocation' && !contract.haulFromLocation) {
                const jAddress = util.object.clone(address);
                jAddress.id = uuid();

                contract.haulFromLocation = jAddress;
            }
        }

        // A hand-entered address isn't one of the dropdown options, so clear
        // the selection for whichever side was edited.
        this.setState({
            contract: { ...contract },
            ...(selectedAddressType === 'haulFromLocation'
                ? { selectedHaulingFromLocation: null }
                : {}),
            ...(selectedAddressType === 'haulToLocation'
                ? { selectedHaulingToLocation: null }
                : {}),
        });
    };

    onHaulFromLocationSelected = (selection) => {
        const { contract } = this.state;
        contract.haulFromLocation = this.haulingLocationToAddress(selection);
        this.setState({
            contract: { ...contract },
            selectedHaulingFromLocation: selection ?? null,
        });
    };

    onHaulToLocationSelected = (selection) => {
        const { contract } = this.state;
        contract.haulToLocation = this.haulingLocationToAddress(selection);
        this.setState({
            contract: { ...contract },
            selectedHaulingToLocation: selection ?? null,
        });
    };

    render() {
        const {
            strings,
            contract,
            contractStatuses,
            contractTemplates,
            customers,
            errors,
            formValidated,
            fromTemplate,
            dispatchLocations,
            operationsCenters,
            originalData,
            saving,
            currentEmailAddress,
            enableContractDateRange,
            warningThresholdOptions,
            enableAutoEscalatingContractRates,
            enablePrevailingWageContracts,
            materials,
            haulingLocations,
            selectedHaulingFromLocation,
            selectedHaulingToLocation
        } = this.state;
        const existing = !!contract.id;

        const crewLead = strings ? strings[l.crewLead] : '';
        const contractTimesheetResponsibilityTypeOptions = this.getContractTimesheetResponsibilityTypeOptions(crewLead);

        // Computed
        const customerHasOperationsCenters = (operationsCenters ?? []).length > 0;

        const isLoading = !!this.state.loading
            || !this.context?.tenant?.tenantSettings
            || !this.context?.user;

        const newContractFromTemplate = Boolean(
            !!fromTemplate && !contract?.id,
        );

        const selfDispatchTimesheetsEnabled = !!this.context?.tenant?.tenantSettings
            ?.enableSelfDispatchTimesheets;

        const showSelfDispatchOptions = !!contract?.allowSelfDispatch // Contract must be set up for self-dispatching
            && !!selfDispatchTimesheetsEnabled; // Tenant setting must also be enabled for self-dispatch timesheets.

        const jContract = JSON.stringify({ ...contract });

        // Rates column will be available depending on whether or not the tenant
        // has billing rates enabled.
        // If you alter the columns here, update this.renderContractChargeTypesRows as well.
        const chargeTypeHeaderLabels = [
            { name: 'Order', class: 'text-center' },
            { name: 'Description' },
            { name: 'Charge Units', class: 'text-center' },
            { name: 'Group' },
            { name: 'Employees' },
            { name: 'Cost Code' },
            { name: 'Billable', class: 'text-center' },
            { name: 'Job Eligibility' },
            { name: '', class: 'text-center' },
        ];

        return (
            <>
                <Prompt
                    when={!saving && !isEqual(originalData, jContract)}
                    message="You have unsaved changes, are you sure you want to leave?"
                />
                <AppPageForm
                    formId="contractForm"
                    formHeadingIcon={faFileContract}
                    formHeading={!existing ? 'New Job' : 'Edit Job'}
                    formName="contractForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    saving={saving}
                    errors={errors}
                    loading={isLoading}
                    loadingMessage="Loading Job, please wait..."
                    onBack={() => this.props.history.push(AppNavPaths.Contracts)}
                    backLabel="Back to Jobs"
                >
                    {!contract.id && (
                        <>
                            <SubHeading className="mt-3">
                                Job Type
                            </SubHeading>
                            <Row className="pt-3">
                                <Col
                                    xl="6"
                                    lg="8"
                                    md="10"
                                    sm="12"
                                    className="ml-auto mr-auto"
                                >
                                    <FormGroup>
                                        <FormLabel
                                            htmlFor="fromTemplate"
                                            text=""
                                        />
                                        <ButtonGroup
                                            id="fromTemplate"
                                            name="fromTemplate"
                                        >
                                            <Button
                                                size="sm"
                                                color={
                                                    fromTemplate === true
                                                        ? 'primary'
                                                        : 'secondary'
                                                }
                                                className={cls('p-2', {
                                                    active:
                                                        fromTemplate === true,
                                                })}
                                                onClick={() => this.setState({
                                                    fromTemplate: true,
                                                })}
                                            >
                                                From Template
                                            </Button>
                                            <Button
                                                size="sm"
                                                color={
                                                    fromTemplate === false
                                                        ? 'primary'
                                                        : 'secondary'
                                                }
                                                className={cls('p-2', {
                                                    active:
                                                        fromTemplate === false,
                                                })}
                                                onClick={() => this.setState({
                                                    fromTemplate: false,
                                                })}
                                            >
                                                Custom
                                            </Button>
                                        </ButtonGroup>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </>
                    )}
                    <SubHeading>Details</SubHeading>
                    <Row>
                        <Col
                            xl="6"
                            lg="8"
                            md="10"
                            sm="12"
                            className="ml-auto mr-auto"
                        >
                            <FormGroup>
                                <FormLabel
                                    htmlFor="customerName"
                                    text="Customer"
                                    required
                                />
                                <ValidatedSelect
                                    id="customers"
                                    name="contract.companyId"
                                    options={customers}
                                    required
                                    value={
                                        (customers ?? []).find(
                                            (s) => s.value === contract.companyId,
                                        ) ?? ''
                                    }
                                    onChange={this.onCustomerChanged}
                                    validationMessage="A customer selection is required."
                                />
                            </FormGroup>
                            <FormGroup
                                hidden={
                                    !customerHasOperationsCenters
                                    || !contract.operationsCentersApply
                                }
                            >
                                <FormLabel
                                    htmlFor="operationsCenter"
                                    text="Operations Center"
                                    required
                                />
                                <ValidatedSelect
                                    id="operationsCenters"
                                    name="contract.operationsCenterCompanyId"
                                    options={operationsCenters}
                                    required={
                                        !!customerHasOperationsCenters
                                        && contract.operationsCentersApply
                                    }
                                    value={
                                        (operationsCenters ?? []).find(
                                            (s) => s.value
                                                === contract.operationsCenterCompanyId,
                                        ) ?? ''
                                    }
                                    onChange={this.onSelectChanged}
                                    validationMessage="A customer selection is required."
                                />
                                <small className="invalid-feedback text-danger">
                                    Operations Center is required.
                                </small>
                            </FormGroup>
                            <FormGroup>
                                <FormLabel
                                    htmlFor="number"
                                    text="Job / Haul Number"
                                    required
                                />
                                <Input
                                    id="number"
                                    name="contract.number"
                                    value={contract.number ?? ''}
                                    onChange={this.onChange}
                                    placeholder="Enter Number (letter and digits only)"
                                    maxLength="150"
                                    pattern="[a-zA-Z0-9\-]+"
                                    type="text"
                                    required
                                />
                                <small className="invalid-feedback text-danger">
                                    Number is required.
                                </small>
                            </FormGroup>
                            <FormGroup>
                                <FormLabel
                                    htmlFor="description"
                                    text="Job Name"
                                    required
                                />
                                <Input
                                    id="description"
                                    name="contract.description"
                                    className="form-control"
                                    value={contract.description ?? ''}
                                    onChange={this.onChange}
                                    placeholder="Enter Name (max 500 characters)"
                                    type="text"
                                    maxLength="500"
                                    required
                                />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel
                                    htmlFor="dispatchLocations"
                                    text="Dispatch Locations"
                                    required
                                />
                                <ValidatedSelect
                                    id="dispatchLocations"
                                    name="contract.dispatchLocations"
                                    options={dispatchLocations}
                                    isMulti
                                    required
                                    value={
                                        (dispatchLocations ?? []).filter((x) => (
                                            contract.dispatchLocations ?? []
                                        ).includes(x.value)) ?? ''
                                    }
                                    onChange={this.onSelectChanged}
                                    validationMessage="A Dispatching selection is required."
                                />
                            </FormGroup>

                            {!contract.allowSelfDispatch &&
                                <FormGroup>
                                    <FormLabel
                                        htmlFor="materials"
                                        text="Materials"
                                        required
                                    />
                                    <ValidatedSelect
                                        id="materials"
                                        name="contract.materials"
                                        options={materials}
                                        isMulti
                                        required
                                        value={
                                            (materials ?? []).filter((x) => (
                                                contract.materials ?? []
                                            ).includes(x.value)) ?? ''
                                        }
                                        onChange={this.onSelectChanged}
                                        validationMessage="A Material selection is required."
                                    />
                                </FormGroup>
                            }
                            <CommonContext.Consumer>
                                {(value) => {
                                    if (!(value ?? {}).tenant) return null;

                                    return (
                                        value.tenant.tenantSettings
                                            .timesheetResponsibilityType
                                        === TimesheetResponsibilityType.Contract && (
                                            <FormGroup>
                                                <FormLabel
                                                    htmlFor="timesheetResponsibility"
                                                    text="Timesheet Responsibility"
                                                    helpMessage={`Timesheet responsibility of INDIVIDUAL will allow individual employees to fill out timesheets.  A setting of ${crewLead.toUpperCase()} will enforce the responsibility of all timesheets to the ${crewLead} for the given day.`}
                                                    required
                                                />
                                                <ValidatedSelect
                                                    id="timesheetResponsibility"
                                                    name="contract.timesheetResponsibilityId"
                                                    options={
                                                        contractTimesheetResponsibilityTypeOptions
                                                    }
                                                    required
                                                    isDisabled={!!contract.id}
                                                    getOptionValue={(option) => option.id}
                                                    getOptionLabel={(option) => option.name}
                                                    value={
                                                        contractTimesheetResponsibilityTypeOptions.find(
                                                            (x) => contract.timesheetResponsibilityTypeId
                                                                === x.id,
                                                        ) ?? ''
                                                    }
                                                    onChange={(item) => this.setState(
                                                        (state) => (
                                                            (state.contract.timesheetResponsibilityTypeId = ((
                                                                item
                                                                ?? {}
                                                            ).id
                                                                ?? -1) > -1
                                                                ? item.id
                                                                : ''),
                                                            state
                                                        ),
                                                    )}
                                                    validationMessage="A timesheet responsibility selection is required."
                                                />
                                            </FormGroup>
                                        )
                                    );
                                }}
                            </CommonContext.Consumer>
                            {/* <FormGroup hidden={!contract.hasAlternativePayroll}> */}
                            {/*    <FormLabel htmlFor="allowAlternativePayroll" text="Use Alternative Payroll" /> */}
                            {/*    <FormCheckbox */}
                            {/*        small={true} */}
                            {/*        className="ml-3 mt-2" */}
                            {/*        id={"allowAlternativePayroll"} */}
                            {/*        checked={contract.useAlternativePayroll} */}
                            {/*        onChange={(e) => { */}
                            {/*            let { contract } = this.state; */}
                            {/*            contract.useAlternativePayroll = e.target.checked; */}
                            {/*            this.setState({ contract: contract }); */}
                            {/*        }} */}
                            {/*        labelText={"Use Alternative Payroll"} */}
                            {/*    /> */}
                            {/* </FormGroup> */}
                            <FormGroup>
                                <FormLabel
                                    htmlFor="contractStatusId"
                                    text="Job Status"
                                    required
                                />
                                <ValidatedSelect
                                    id="contractStatusId"
                                    name="contract.contractStatusId"
                                    options={contractStatuses}
                                    required
                                    value={
                                        (contractStatuses ?? []).find(
                                            (x) => contract.contractStatusId
                                                === x.value,
                                        ) ?? ''
                                    }
                                    onChange={this.onSelectChanged}
                                    validationMessage="Account status selection is required."
                                />
                            </FormGroup>

                            {(!fromTemplate || !!contract?.id) && (
                                <>

                                    {false && ( // for now, remove this choice until self-dispatch without non-flagging is fully implemented.
                                        <FormGroup>
                                            <FormLabel
                                                htmlFor="allowSelfDispatch"
                                                text="Allow Self-Dispatch?"
                                                required
                                            />
                                            <ButtonGroup
                                                id="allowSelfDispatch"
                                                name="allowSelfDispatch"
                                            >
                                                {this.state.selfDispatchOptions.map(
                                                    (t) => (
                                                        <Button
                                                            key={t.value}
                                                            size="sm"
                                                            color={
                                                                t.value
                                                                    === contract.allowSelfDispatch
                                                                    ? 'primary'
                                                                    : 'secondary'
                                                            }
                                                            className={cls(
                                                                'p-2',
                                                                {
                                                                    active:
                                                                        t.value
                                                                        === contract.allowSelfDispatch,
                                                                },
                                                            )}
                                                            onClick={() => {
                                                                this.onAllowSelfDispatchChanged(
                                                                    t.value,
                                                                );
                                                            }}
                                                        >
                                                            {t.label}
                                                        </Button>
                                                    ),
                                                )}
                                            </ButtonGroup>
                                        </FormGroup>
                                    )}
                                    {false && /* Hide for now, until self dispatch has client signature added. */ !!showSelfDispatchOptions && (
                                        <FormGroup>
                                            <FormLabel
                                                htmlFor="requireSelfDispatchTimesheetsClientSignature"
                                                text="Require Client Signatures on Self-Dispatching Timesheets?"
                                                required
                                            />
                                            <ButtonGroup
                                                id="requireSelfDispatchTimesheetsClientSignature"
                                                name="requireSelfDispatchTimesheetsClientSignature"
                                            >
                                                {this.state.selfDispatchOptions.map(
                                                    (t) => (
                                                        <Button
                                                            key={t.value}
                                                            size="sm"
                                                            color={
                                                                t.value
                                                                    === contract.requireSelfDispatchTimesheetsClientSignature
                                                                    ? 'primary'
                                                                    : 'secondary'
                                                            }
                                                            className={cls(
                                                                'p-2',
                                                                {
                                                                    active:
                                                                        t.value
                                                                        === contract.requireSelfDispatchTimesheetsClientSignature,
                                                                },
                                                            )}
                                                            onClick={() => {
                                                                this.onRequireSelfDispatchTimesheetsClientSignatureChanged(
                                                                    t.value,
                                                                );
                                                            }}
                                                        >
                                                            {t.label}
                                                        </Button>
                                                    ),
                                                )}
                                            </ButtonGroup>
                                        </FormGroup>
                                    )}
                                    {!!selfDispatchTimesheetsEnabled && 1 == 2 && (
                                        <FormGroup>
                                            <FormLabel
                                                htmlFor="enableNonFlaggingTimesheets"
                                                text="Is this a Non-Flagging Contract?"
                                                required
                                            />
                                            <ButtonGroup
                                                id="enableNonFlaggingTimesheets"
                                                name="enableNonFlaggingTimesheets"
                                            >
                                                {this.state.selfDispatchOptions.map(
                                                    (t) => (
                                                        <Button
                                                            key={t.value}
                                                            size="sm"
                                                            color={
                                                                t.value
                                                                    === contract.enableNonFlaggingTimesheets
                                                                    ? 'primary'
                                                                    : 'secondary'
                                                            }
                                                            className={cls(
                                                                'p-2',
                                                                {
                                                                    active:
                                                                        t.value
                                                                        === contract.enableNonFlaggingTimesheets,
                                                                },
                                                            )}
                                                            onClick={() => {
                                                                this.onAllowNonFlaggingChanged(
                                                                    t.value,
                                                                );
                                                            }}
                                                        >
                                                            {t.label}
                                                        </Button>
                                                    ),
                                                )}
                                            </ButtonGroup>
                                        </FormGroup>
                                    )}

                                    <FormGroup>
                                        <FormLabel
                                            htmlFor="workOrderTypeId"
                                            text="Ticket Number?"
                                            required
                                        />
                                        <ValidatedSelect
                                            id="workOrderTypeId"
                                            name="contract.workOrderTypeId"
                                            options={WorkOrderNumberOptions}
                                            required
                                            value={
                                                WorkOrderNumberOptions.find(
                                                    (wt) => contract.workOrderTypeId
                                                        === wt.value,
                                                ) ?? ''
                                            }
                                            onChange={this.onSelectChanged}
                                            validationMessage="A ticket number selection is required."
                                        />
                                    </FormGroup>

                                    <Row>
                                        <Col>
                                            <FormGroup>
                                                {contract.haulFromLocation &&
                                                    <>
                                                        <table>
                                                            <tr>
                                                                <td>
                                                                    <FormLabel
                                                                        htmlFor="meetingLocation"
                                                                        text="Default Haul From"
                                                                    />
                                                                </td>
                                                                <td align="right">
                                                                    <div className="bold-link" onClick={() => { this.onEditLocation('globalLocation', 'haulingFromLocation') }}>
                                                                        <i className="fa fa-plus-circle"></i>
                                                                        &nbsp;Add Location
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        </table>

                                                        <ValidatedSelect
                                                            id="haulingFromLocation"
                                                            name="haulingFromLocation"
                                                            options={haulingLocations}
                                                            getOptionLabel={this.haulingLocationLabel}
                                                            getOptionValue={(option) => option.id}
                                                            value={
                                                                (haulingLocations ?? []).find(
                                                                    (x) => x.id
                                                                        === selectedHaulingFromLocation?.id,
                                                                ) ?? ''
                                                            }
                                                            onChange={this.onHaulFromLocationSelected}
                                                        />

                                                        <div style={{ height: '5px' }}>&nbsp;</div>

                                                        <FlexStartRow>
                                                            <SmallOutlineButton
                                                                onClick={() => {
                                                                    this.onEditLocation(
                                                                        'haulFromLocation',
                                                                    );
                                                                }}
                                                                text="Edit"
                                                            />

                                                            <span className="form-control-plaintext">
                                                                {contract.haulFromLocation.gpsOnly
                                                                    ? '(GPS Only)'
                                                                    : <>
                                                                        {contract.haulFromLocation.address1}
                                                                        {' '}

                                                                        {contract.haulFromLocation.city}
                                                                        {', '}

                                                                        {contract.haulFromLocation.state}
                                                                        {', '}

                                                                        {contract.haulFromLocation.zip}
                                                                        {' '}
                                                                    </>
                                                                }
                                                            </span>

                                                        </FlexStartRow>


                                                    </>
                                                }

                                                {!contract.haulFromLocation &&

                                                    <>
                                                        <table>
                                                            <tr>
                                                                <td>
                                                                    <FormLabel
                                                                        htmlFor="meetingLocation"
                                                                        text="Default Haul From"
                                                                    />
                                                                </td>
                                                                <td align="right">
                                                                    <div className="bold-link" onClick={() => { this.onEditLocation('globalLocation', 'haulingFromLocation') }}>
                                                                        <i className="fa fa-plus-circle"></i>
                                                                        &nbsp;Add Location
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        </table>

                                                        <ValidatedSelect
                                                            id="haulingFromLocation"
                                                            name="haulingFromLocation"
                                                            options={haulingLocations}
                                                            getOptionLabel={this.haulingLocationLabel}
                                                            getOptionValue={(option) => option.id}
                                                            value={
                                                                (haulingLocations ?? []).find(
                                                                    (x) => x.id
                                                                        === selectedHaulingFromLocation?.id,
                                                                ) ?? ''
                                                            }
                                                            onChange={this.onHaulFromLocationSelected}
                                                        />
                                                        <div style={{ marginTop: '6px', textAlign: 'right' }} className="site-link" onClick={() => this.onAddLocation('haulFromLocation')}>
                                                            Edit Address
                                                        </div>
                                                    </>
                                                }

                                            </FormGroup>

                                        </Col>
                                        <Col>
                                            <FormGroup>
                                                {contract.haulToLocation &&
                                                    <>
                                                        <table>
                                                            <tr>
                                                                <td>
                                                                    <FormLabel
                                                                        htmlFor="meetingLocation"
                                                                        text="Default Haul To"
                                                                    />
                                                                </td>
                                                                <td align="right">
                                                                    <div className="bold-link" onClick={() => { this.onEditLocation('globalLocation', 'haulingToLocation') }}>
                                                                        <i className="fa fa-plus-circle"></i>
                                                                        &nbsp;Add Location
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        </table>

                                                        <ValidatedSelect
                                                            id="haulingToLocation"
                                                            name="haulingToLocation"
                                                            options={haulingLocations}
                                                            getOptionLabel={this.haulingLocationLabel}
                                                            getOptionValue={(option) => option.id}
                                                            value={
                                                                (haulingLocations ?? []).find(
                                                                    (x) => x.id
                                                                        === selectedHaulingToLocation?.id,
                                                                ) ?? ''
                                                            }
                                                            onChange={this.onHaulToLocationSelected}
                                                        />

                                                        <div style={{ height: '5px' }}>&nbsp;</div>

                                                        <FlexStartRow>
                                                            <SmallOutlineButton
                                                                onClick={() => {
                                                                    this.onEditLocation(
                                                                        'haulToLocation',
                                                                    );
                                                                }}
                                                                text="Edit"
                                                            />

                                                            <span className="form-control-plaintext">
                                                                {contract.haulToLocation.gpsOnly
                                                                    ? '(GPS Only)'
                                                                    : <>
                                                                        {contract.haulToLocation.address1}
                                                                        {' '}

                                                                        {contract.haulToLocation.city}
                                                                        {', '}

                                                                        {contract.haulToLocation.state}
                                                                        {', '}

                                                                        {contract.haulToLocation.zip}
                                                                        {' '}
                                                                    </>
                                                                }
                                                            </span>

                                                        </FlexStartRow>


                                                    </>
                                                }

                                                {!contract.haulToLocation &&
                                                    <>
                                                        <table>
                                                            <tr>
                                                                <td>
                                                                    <FormLabel
                                                                        htmlFor="meetingLocation"
                                                                        text="Default Haul To"
                                                                    />
                                                                </td>
                                                                <td align="right">
                                                                    <div className="bold-link" onClick={() => { this.onEditLocation('globalLocation', 'haulingToLocation') }}>
                                                                        <i className="fa fa-plus-circle"></i>
                                                                        &nbsp;Add Location
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        </table>

                                                        <ValidatedSelect
                                                            id="haulingToLocation"
                                                            name="haulingToLocation"
                                                            options={haulingLocations}
                                                            getOptionLabel={this.haulingLocationLabel}
                                                            getOptionValue={(option) => option.id}
                                                            value={
                                                                (haulingLocations ?? []).find(
                                                                    (x) => x.id
                                                                        === selectedHaulingToLocation?.id,
                                                                ) ?? ''
                                                            }
                                                            onChange={this.onHaulToLocationSelected}
                                                        />
                                                        <div style={{ marginTop: '6px', textAlign: 'right' }} className="site-link" onClick={() => this.onAddLocation('haulToLocation')}>
                                                            Edit Address
                                                        </div>
                                                    </>
                                                }

                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <FormGroup
                                        hidden={
                                            !contract.workOrderTypeId
                                            || (!!contract.workOrderTypeId
                                                && contract.workOrderTypeId
                                                === ChargeTypeEligbility.DoNotUse)
                                        }
                                    >
                                        <FormLabel
                                            htmlFor="linkedWorkOrders"
                                            text="Allow Addition of Scale Tickets?"
                                            required={
                                                !!contract.workOrderTypeId
                                            }
                                        />
                                        <ButtonGroup
                                            id="allowLinkedWorkOrders"
                                            name="contract.allowLinkedWorkOrders"
                                        >
                                            {this.state.selfDispatchOptions.map(
                                                (t) => (
                                                    <Button
                                                        key={t.value}
                                                        size="sm"
                                                        color={
                                                            t.value
                                                                === contract.allowLinkedWorkOrders
                                                                ? 'primary'
                                                                : 'secondary'
                                                        }
                                                        className={cls('p-2', {
                                                            active:
                                                                t.value
                                                                === contract.allowLinkedWorkOrders,
                                                        })}
                                                        onClick={() => {
                                                            this.onAllowLinkedWorkOrdersChanged(
                                                                t.value,
                                                            );
                                                        }}
                                                    >
                                                        {t.label}
                                                    </Button>
                                                ),
                                            )}
                                        </ButtonGroup>
                                    </FormGroup>

                                    {enableContractDateRange && 1 == 2 && (
                                        <>
                                            <FormGroup>
                                                <FormLabel
                                                    htmlFor="effectiveDate"
                                                    text="Effective Date"
                                                    required
                                                />
                                                <input
                                                    id="effectiveDate"
                                                    required
                                                    type="date"
                                                    className="form-control"
                                                    name="contract.effectiveDate"
                                                    defaultValue={
                                                        contract.effectiveDate
                                                        ?? ''
                                                    }
                                                    onChange={this.onChange}
                                                />
                                                <small className="invalid-feedback text-danger">
                                                    Effective Date is required.
                                                </small>
                                            </FormGroup>

                                            <FormGroup>
                                                <FormLabel
                                                    htmlFor="expirationDate"
                                                    text="Expiration Date"
                                                    required
                                                />
                                                <input
                                                    id="expirationDate"
                                                    required
                                                    type="date"
                                                    className="form-control"
                                                    name="contract.expirationDate"
                                                    defaultValue={
                                                        contract.expirationDate
                                                        ?? ''
                                                    }
                                                    onChange={this.onChange}
                                                />
                                                <small className="invalid-feedback text-danger">
                                                    Expiration Date is required.
                                                </small>
                                            </FormGroup>

                                            <FormGroup>
                                                <FormLabel
                                                    htmlFor="warningThresholdInDays"
                                                    text="Warning Threshold (Days)"
                                                    required
                                                />
                                                <ValidatedSelect
                                                    id="warningThresholdInDays"
                                                    name="contract.warningThresholdInDays"
                                                    options={
                                                        warningThresholdOptions
                                                    }
                                                    required
                                                    value={
                                                        (
                                                            warningThresholdOptions
                                                            ?? []
                                                        ).find(
                                                            (x) => contract.warningThresholdInDays
                                                                === x.value,
                                                        ) ?? ''
                                                    }
                                                    getOptionLabel={(option) => option.label}
                                                    getOptionValue={(option) => option.value}
                                                    onChange={(selection) => {
                                                        const value = selection?.value;
                                                        this.setState(
                                                            (state) => (
                                                                (contract.warningThresholdInDays = value),
                                                                { ...state }
                                                            ),
                                                        );
                                                    }}
                                                    validationMessage="Warning Threshold is required."
                                                    hideClear
                                                />
                                            </FormGroup>
                                        </>
                                    )}
                                </>
                            )}
                            {newContractFromTemplate && (
                                <>
                                    <div className="pt-1" />
                                    <hr />
                                    <FormGroup>
                                        <FormLabel
                                            htmlFor="contractTemplateId"
                                            text="Template"
                                            required
                                        />
                                        <ValidatedSelect
                                            id="contractStatusId"
                                            name="contract.contractTemplateId"
                                            options={contractTemplates}
                                            required
                                            value={
                                                (contractTemplates ?? []).find(
                                                    (x) => contract.contractTemplateId
                                                        === x.value,
                                                ) ?? ''
                                            }
                                            getOptionLabel={(option) => option.key}
                                            getOptionValue={(option) => option.value}
                                            onChange={(selection) => {
                                                const value = selection?.value;
                                                this.setState(
                                                    (state) => (
                                                        (contract.contractTemplateId = value),
                                                        { ...state }
                                                    ),
                                                );
                                            }}
                                            validationMessage="Template selection is required."
                                        />
                                    </FormGroup>
                                </>
                            )}

                            {enableAutoEscalatingContractRates && 1 == 2 && (
                                <FormGroup>
                                    <FormLabel
                                        htmlFor="autoEscalatingRates"
                                        text="Auto-Escalating Rates"
                                    />
                                    <ButtonGroup
                                        id="autoEscalatingRates"
                                        name="contract.autoEscalatingRates"
                                    >
                                        {this.state.autoEscalatingRateOptions.map(
                                            (t) => (
                                                <Button
                                                    key={t.value}
                                                    size="sm"
                                                    color={
                                                        t.value
                                                            === contract.autoEscalatingRates
                                                            ? 'primary'
                                                            : 'secondary'
                                                    }
                                                    className={cls('p-2', {
                                                        active:
                                                            t.value
                                                            === contract.autoEscalatingRates,
                                                    })}
                                                    onClick={() => {
                                                        this.onAutoEscalatingRatesChanged(
                                                            t.value,
                                                        );
                                                    }}
                                                >
                                                    {t.label}
                                                </Button>
                                            ),
                                        )}
                                    </ButtonGroup>
                                </FormGroup>
                            )}

                            {enablePrevailingWageContracts && 1 == 2 && (
                                <FormGroup>
                                    <FormLabel
                                        htmlFor="prevailingWage"
                                        text="Prevailing Wage"
                                    />
                                    <ButtonGroup
                                        id="prevailingWage"
                                        name="contract.prevailingWage"
                                    >
                                        {this.state.prevailingWageOptions.map(
                                            (t) => (
                                                <Button
                                                    key={t.value}
                                                    size="sm"
                                                    color={
                                                        t.value
                                                            === contract.prevailingWage
                                                            ? 'primary'
                                                            : 'secondary'
                                                    }
                                                    className={cls('p-2', {
                                                        active:
                                                            t.value
                                                            === contract.prevailingWage,
                                                    })}
                                                    onClick={() => {
                                                        this.onPrevailingWageChanged(
                                                            t.value,
                                                        );
                                                    }}
                                                >
                                                    {t.label}
                                                </Button>
                                            ),
                                        )}
                                    </ButtonGroup>
                                </FormGroup>
                            )}

                            {/*<FormGroup>*/}
                            {/*    <FormLabel*/}
                            {/*        htmlFor="setupCharges"*/}
                            {/*        text="Setup Charges Required"*/}
                            {/*    />*/}
                            {/*    <ButtonGroup*/}
                            {/*        id="setupCharges"*/}
                            {/*        name="contract.setupChargesRequired"*/}
                            {/*    >*/}
                            {/*        {this.state.prevailingWageOptions.map(*/}
                            {/*            (t) => (*/}
                            {/*                <Button*/}
                            {/*                    key={t.value}*/}
                            {/*                    size="sm"*/}
                            {/*                    color={*/}
                            {/*                        t.value*/}
                            {/*                            === contract.setupChargesRequired*/}
                            {/*                            ? 'primary'*/}
                            {/*                            : 'secondary'*/}
                            {/*                    }*/}
                            {/*                    className={cls('p-2', {*/}
                            {/*                        active:*/}
                            {/*                            t.value*/}
                            {/*                            === contract.setupChargesRequired,*/}
                            {/*                    })}*/}
                            {/*                    onClick={() => {*/}
                            {/*                        this.onSetupChargesRequiredChanged(*/}
                            {/*                            t.value,*/}
                            {/*                        );*/}
                            {/*                    }}*/}
                            {/*                >*/}
                            {/*                    {t.label}*/}
                            {/*                </Button>*/}
                            {/*            ),*/}
                            {/*        )}*/}
                            {/*    </ButtonGroup>*/}
                            {/*</FormGroup>*/}

                            {/*<FormGroup>*/}
                            {/*    <FormLabel*/}
                            {/*        htmlFor="permitFeesApply"*/}
                            {/*        text="Permit Fees Apply"*/}
                            {/*    />*/}
                            {/*    <ButtonGroup*/}
                            {/*        id="permitFeesApply"*/}
                            {/*        name="contract.permitFeesApply"*/}
                            {/*    >*/}
                            {/*        {this.state.prevailingWageOptions.map(*/}
                            {/*            (t) => (*/}
                            {/*                <Button*/}
                            {/*                    key={t.value}*/}
                            {/*                    size="sm"*/}
                            {/*                    color={*/}
                            {/*                        t.value*/}
                            {/*                            === contract.permitFeesApply*/}
                            {/*                            ? 'primary'*/}
                            {/*                            : 'secondary'*/}
                            {/*                    }*/}
                            {/*                    className={cls('p-2', {*/}
                            {/*                        active:*/}
                            {/*                            t.value*/}
                            {/*                            === contract.permitFeesApply,*/}
                            {/*                    })}*/}
                            {/*                    onClick={() => {*/}
                            {/*                        this.onPermitFeesApplyChanged(*/}
                            {/*                            t.value,*/}
                            {/*                        );*/}
                            {/*                    }}*/}
                            {/*                >*/}
                            {/*                    {t.label}*/}
                            {/*                </Button>*/}
                            {/*            ),*/}
                            {/*        )}*/}
                            {/*    </ButtonGroup>*/}
                            {/*</FormGroup>*/}

                        </Col>
                    </Row>
                    {!!this.context?.tenant?.tenantSettings?.timesheetEmailEnabled && (
                        <Row className="pt-3 pb-3">
                            <Col
                                xl="8"
                                lg="8"
                                md="10"
                                sm="12"
                                className="ml-auto mr-auto"
                            >
                                <FormGroup>
                                    <FormLabel
                                        className="w-100 contract-email-label"
                                        htmlFor="workOrderTypeId"
                                        text="Timesheet Email Recipients"
                                    />
                                    <small className="pl-2 pb-2 text-success">
                                        Enter addresses at which you would like
                                        to receive copies of contract-related
                                        timesheets below.
                                    </small>
                                    <EmailRecipients
                                        currentEmailAddress={
                                            currentEmailAddress
                                        }
                                        onEmailAdded={this.onEmailAdded}
                                        onEmailChanged={this.onEmailChanged}
                                        onEmailRemoved={this.onEmailRemoved}
                                        emailAddresses={
                                            contract.timesheetEmailRecipients
                                        }
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    )}

                    {!!contract.id && contract.prevailingWage && (
                        <Row>
                            <Col>
                                <SubHeading>Pay Schedules</SubHeading>
                                <SimpleTable
                                    className="mb-0"
                                    id="contractPrevailingWageTable"
                                    onAddFunction={this.onAddPaySchedules}
                                    addLabelText={
                                        this.context.formIsOpen ? (
                                            <span>
                                                <i className="fa fa-spin fa-circle-notch mr-1" />
                                                Opening pay schedules
                                                form...
                                            </span>
                                        ) : (
                                            'Add Pay Schedule'
                                        )
                                    }
                                    noTopBorder
                                    permission="contract"
                                    tableHeaderLabels={[
                                        {
                                            name: 'Description',
                                            class: 'ct-job-number',
                                        },
                                        {
                                            name: 'State',
                                            class: 'ct-job-name',
                                        },
                                        {
                                            name: 'Counties',
                                            class: 'ct-job-week',
                                        },
                                        {
                                            name: 'Reg',
                                            class: 'ct-job-active',
                                        },
                                        {
                                            name: 'OT',
                                            class: 'ct-job-active',
                                        },
                                        {
                                            name: 'Fringe',
                                            class: 'ct-job-active',
                                        },
                                        {
                                            name: '',
                                            class: 'ct-job-active',
                                        },
                                    ]}
                                    editable={false}
                                    entities={contract.contractPaySchedules}
                                    rowRenderer={
                                        this.renderContractPaySchedules
                                    }
                                    noDataText="No pay schedules found."
                                />
                            </Col>
                        </Row>
                    )}
                    <p />
                    {!!contract.id && (
                        <>
                            <Row>
                                <Col>
                                    <SubHeading>Charge Types</SubHeading>
                                    <SimpleTable
                                        className="mb-0"
                                        id="contractChargeTypesTable"
                                        onAddFunction={this.onAddChargeTypes}
                                        addButtonEnabled={
                                            parseInt(
                                                this.state.contract.companyId
                                                ?? 0,
                                            ) > 0
                                        }
                                        addLabelText={
                                            this.context.formIsOpen ? (
                                                <span>
                                                    <i className="fa fa-spin fa-circle-notch mr-1" />
                                                    Opening charge types form...
                                                </span>
                                            ) : (
                                                'Add Charge Types'
                                            )
                                        }
                                        leftToolbarContent={
                                            !!(
                                                (this.state.contract ?? {})
                                                    .contractChargeTypes ?? []
                                            ).length && (
                                                <SmallButton
                                                    type="button"
                                                    disabled={
                                                        !!this.context
                                                            .formIsOpen
                                                    }
                                                    onClick={
                                                        this
                                                            .onReorderChargeTypes
                                                    }
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faSort}
                                                        className="mr-2"
                                                    />
                                                    Reorder
                                                </SmallButton>
                                            )
                                        }
                                        noTopBorder
                                        permission="contract"
                                        tableHeaderLabels={
                                            chargeTypeHeaderLabels
                                        }
                                        editable={false}
                                        entities={contract.contractChargeTypes}
                                        rowRenderer={
                                            this.renderContractChargeTypesRows
                                        }
                                        noDataText="No charge types found."
                                    />
                                </Col>
                            </Row>

                            {!!(contract?.contractChargeTypes ?? []).length && (
                                <Row>
                                    <Col>
                                        <FlexEndRow className="pt-1 pl-3 pr-4">
                                            <small
                                                className="site-link w-100 text-right"
                                                onClick={() => {
                                                    const { contract } = {
                                                        ...this.state,
                                                    };
                                                    const {
                                                        contractChargeTypes,
                                                    } = { ...contract };
                                                    const updated = [
                                                        ...contractChargeTypes.map(
                                                            (x) => ({
                                                                ...x,
                                                                chargeTypeEligibilityId:
                                                                    _ContractChargeTypeEligibility.Required,
                                                            }),
                                                        ),
                                                    ];
                                                    contract.contractChargeTypes = updated;
                                                    this.setState({
                                                        contract: {
                                                            ...contract,
                                                        },
                                                    });
                                                }}
                                            >
                                                Require All Charge Types
                                            </small>
                                        </FlexEndRow>
                                    </Col>
                                </Row>
                            )}

                            {contract?.contractChargeTypes.length == 0 &&
                                <p>&nbsp;</p>
                            }

                            {!showSelfDispatchOptions && contract?.contractChargeTypes.length > 0 && (
                                <Row>
                                    <Col>
                                        <SubHeading>Orders</SubHeading>
                                        <SimpleTable
                                            id="contractJobTable"
                                            scrollable
                                            maxBodyHeight="500px"
                                            noTopBorder
                                            permission="contract"
                                            addLabelText="Add Order"
                                            addButtonEnabled={
                                                contract?.contractStatusId
                                                === _ContractStatuses.Active
                                            }
                                            onAddFunction={this.onAddJob}
                                            tableHeaderLabels={[
                                                {
                                                    name: 'Order #',
                                                    class: 'ct-job-number',
                                                },
                                                {
                                                    name: 'Order Name',
                                                    class: 'ct-job-name',
                                                },
                                                {
                                                    name: 'Week',
                                                    class: 'ct-job-week',
                                                },
                                                {
                                                    name: 'Active?',
                                                    class: 'ct-job-active',
                                                },
                                            ]}
                                            editable={false}
                                            entities={this.state.contract.jobs.filter(
                                                (x) => x.isDeleted == false,
                                            )}
                                            rowRenderer={this.renderJobRows}
                                            noDataText="No orders found."
                                            leftToolbarContent={(
                                                <span className="pl-1 text-muted font-weight-bold">
                                                    Most recent 2 order weeks
                                                    <Link
                                                        className="ml-2 site-link"
                                                        to={`${AppNavPaths.Jobs}?contractNumber=${this.state.contract.number}`}
                                                    >
                                                        (View All)
                                                    </Link>
                                                </span>
                                            )}
                                        />
                                    </Col>
                                </Row>
                            )}
                        </>
                    )}
                    <FlexCenterRow className="mb-3">
                        <Button
                            size="sm"
                            type="submit"
                            color="primary"
                            name="contractForm"
                        >
                            <FontAwesomeIcon className="mr-2" icon={faSave} />
                            {existing ? 'Save' : 'Save New Order'}
                        </Button>
                    </FlexCenterRow>
                </AppPageForm>
                <ChargeTypeSelect
                    ref={this.chargeTypeSelectorRef}
                    id="chargeTypeSelector"
                    onAddCallback={this.onAddChargeTypesCallback}
                />

                <AddressFormNew
                    id="jobAddressForm"
                    ref={this.addressFormRef}
                    contractUsesPrevailingWage={contract.prevailingWage}
                    onSaveCallback={(address) => this.onSaveAddress(address)}
                />

                <SlideForm
                    loading={false}
                    show={this.state.showReorderWidget}
                    id="reorderChargeTypesWidget"
                    formIcon={faFileContract}
                    formTitle="Reorder Charge Types"
                    ref={this.chargeTypesWidgetRef}
                    onClose={() => {
                        this.setState(
                            (state) => (
                                (state.showReorderWidget = false), state
                            ),
                        );
                    }}
                >
                    <ReorderListWidget
                        entities={this.state.selectedSortableChargeTypes}
                        entityName="description"
                        customColors={false}
                        setUpdatedEntities={(items) => {
                            this.setState({
                                selectedSortableChargeTypes: items,
                            });
                        }}
                        ref={this.chargeTypesWidgetRef}
                        onSaveCallback={() => {
                            const { contract, selectedSortableChargeTypes } = {
                                ...this.state,
                            };
                            let updated = [...selectedSortableChargeTypes];
                            updated = updated.map((x, index) => ({ ...x, ...{ order: (index += 1) } }));
                            contract.contractChargeTypes = updated;
                            this.setState({
                                contract,
                                showReorderWidget: false,
                            });
                        }}
                    />
                </SlideForm>
            </>
        );
    }
}
export default withRouter(ContractForm);